import { z } from "zod";
import { StateMachine } from "../utils/state-machine.utils.js";
import { StatefulValue } from "../utils/stateful-value.utils.js";
import { InvalidParticipantStateError } from "../errors/participant-state.error.js";

export const EventParticipantStatusSchema = z.enum({
  PENDING: "PENDING",
  INVITED: "INVITED",
  CONTACTED: "CONTACTED",
  CONFIRMED: "CONFIRMED",
  JOINED: "JOINED",
  REMOVED: "REMOVED",
});

export type EventParticipantStatusType = z.infer<
  typeof EventParticipantStatusSchema
>;

export class ParticipantState extends StatefulValue<EventParticipantStatusType> {
  static readonly values = EventParticipantStatusSchema.enum;

  private static readonly TERMINAL_STATUSES: readonly EventParticipantStatusType[] =
    ["REMOVED"] as const;

  private static readonly TRANSITIONS_DATA: ReadonlyArray<
    readonly [EventParticipantStatusType, readonly EventParticipantStatusType[]]
  > = [
    ["PENDING", ["INVITED", "CONTACTED", "REMOVED"]],
    ["INVITED", ["CONTACTED", "JOINED", "REMOVED"]],
    ["CONTACTED", ["CONFIRMED", "REMOVED"]],
    ["CONFIRMED", ["JOINED", "REMOVED"]],
    ["JOINED", ["REMOVED"]],
    ["REMOVED", []],
  ];

  private static readonly stateMachine = new StateMachine(
    ParticipantState.TRANSITIONS_DATA,
    ParticipantState.TERMINAL_STATUSES,
  );

  static readonly PENDING = new ParticipantState(
    ParticipantState.values.PENDING,
  );
  static readonly INVITED = new ParticipantState(
    ParticipantState.values.INVITED,
  );
  static readonly CONTACTED = new ParticipantState(
    ParticipantState.values.CONTACTED,
  );
  static readonly CONFIRMED = new ParticipantState(
    ParticipantState.values.CONFIRMED,
  );
  static readonly JOINED = new ParticipantState(ParticipantState.values.JOINED);
  static readonly REMOVED = new ParticipantState(
    ParticipantState.values.REMOVED,
  );

  private constructor(protected readonly value: EventParticipantStatusType) {
    super(ParticipantState.stateMachine, value);
  }

  static fromString(value: string): ParticipantState {
    return StatefulValue.lookupOrThrow(
      ParticipantState,
      value,
      (v) => new InvalidParticipantStateError(v),
    );
  }
}
